// weekly-deals

if (!customElements.get('weekly-deals')) {
  class WeeklyDeals extends HTMLElement {
    constructor() {
      super()
      this.intervalId = null
    }

    connectedCallback() {
      // Wait for DOM to be fully loaded before initializing
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init())
      } else {
        this.init()
      }
    }

    disconnectedCallback() {
      // Clean up the interval when element is removed
      if (this.intervalId) {
        clearInterval(this.intervalId)
      }
    }

    init() {
      const timerContainer = this.querySelector('.js-timer-container')
      const hoursLeftElement = timerContainer?.querySelector('.js-hours-left')
      const minutesLeftElement = timerContainer?.querySelector('.js-minutes-left')
      const secondsLeftElement = timerContainer?.querySelector('.js-seconds-left')
      const weeklyDealsProducts = this.querySelectorAll('.js-deals-product')
      const discountDataElement = this.querySelector('.js-discount-data')

      if (!timerContainer || !hoursLeftElement || !minutesLeftElement || !secondsLeftElement) {
        return
      }

      // Start the timer
      this.intervalId = setInterval(
        () => this.updateTimer(hoursLeftElement, minutesLeftElement, secondsLeftElement),
        1000,
      )

      if (!discountDataElement) {
        return
      }

      const parsedDiscountData = JSON.parse(discountDataElement.innerText)
      this.discountCodeName = parsedDiscountData.discount_code_name
      this.discountCodePercent = parsedDiscountData.discount_code_percent
      this.discountQuantityTotal = parsedDiscountData.discount_quantity_total
      this.discountQuantityLeft = parsedDiscountData.discount_quantity_left
      this.discountQuantityClaimed = parsedDiscountData.discount_quantity_claimed
      this.discountPercentClaimed = parsedDiscountData.discount_percent_claimed
      this.discountEndDate = parsedDiscountData.discount_end_date

      if (!weeklyDealsProducts) return

      // Handle department-based sorting for weekly-deals page
      if (window.location.pathname.includes('pages/weekly-deals')) {
        this.sortProductsByDepartment(weeklyDealsProducts)
      }

      weeklyDealsProducts.forEach((product, index) => {
        const productHandle = product.dataset.productHandle
        const claimDealButtons = product.querySelectorAll('.js-claim-deal-button')
        const claimedProduct = product.querySelector('.js-claimed-product')
        const progressBarCompleted = claimedProduct?.querySelector('.js-progress-bar-completed')
        const claimedText = claimedProduct?.querySelector('.js-claimed-text')
        const actualPercentClaimed = this.discountPercentClaimed * 1.0
        let adjustedPercentClaimed = actualPercentClaimed

        if (!actualPercentClaimed) return

        // Determine product position (1-based)
        const position = index + 1

        function isPrime(n) {
          if (n < 2) return false
          for (let i = 2; i <= Math.sqrt(n); i++) if (n % i === 0) return false
          return true
        }

        const handleOffset =
          ([...productHandle].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % 8) - 2
        adjustedPercentClaimed += handleOffset

        // Every third product: reduce by 3
        if (position % 3 === 0) adjustedPercentClaimed -= 3
        // Prime-numbered product: increase by 2
        if (isPrime(position)) adjustedPercentClaimed += 2

        // Clamp between 0 and 95
        if (adjustedPercentClaimed < 0) adjustedPercentClaimed = 0
        if (adjustedPercentClaimed > 95) adjustedPercentClaimed = 99

        if (claimedText) claimedText.innerText = adjustedPercentClaimed + '% claimed'
        if (progressBarCompleted) progressBarCompleted.style.width = adjustedPercentClaimed + '%'

        if (claimDealButtons) {
          claimDealButtons.forEach((button) => {
            button.addEventListener('click', () => {
              this.handleClaimDealButtonClick(productHandle)
            })
          })
        }
      })
    }

    sortProductsByDepartment(products) {
      const urlParams = new URLSearchParams(window.location.search)
      const depParam = urlParams.get('dep')
      const productsContainer = this.querySelector('.deals__body--products')

      if (!productsContainer) return

      // Convert NodeList to Array for sorting
      const productsArray = Array.from(products)

      // If no dep param, just sort to put app exclusive at end and fade in
      if (!depParam || (depParam !== 'womens' && depParam !== 'kids')) {
        productsArray.sort((a, b) => {
          const aIsAppExclusive = a.dataset.isAppExclusive === 'true'
          const bIsAppExclusive = b.dataset.isAppExclusive === 'true'

          // App exclusive products go to the end
          if (aIsAppExclusive && !bIsAppExclusive) return 1
          if (!aIsAppExclusive && bIsAppExclusive) return -1
          return 0
        })

        productsArray.forEach((product) => {
          productsContainer.appendChild(product)
        })

        productsContainer.style.opacity = '1'
        return
      }

      // Sort products based on department, with app exclusive always at end
      productsArray.sort((a, b) => {
        const aIsAppExclusive = a.dataset.isAppExclusive === 'true'
        const bIsAppExclusive = b.dataset.isAppExclusive === 'true'

        // App exclusive products always go to the end
        if (aIsAppExclusive && !bIsAppExclusive) return 1
        if (!aIsAppExclusive && bIsAppExclusive) return -1

        // If neither or both are app exclusive, sort by department
        const aHasDep =
          a.dataset[`has${depParam.charAt(0).toUpperCase() + depParam.slice(1)}`] === 'true'
        const bHasDep =
          b.dataset[`has${depParam.charAt(0).toUpperCase() + depParam.slice(1)}`] === 'true'

        // Products with matching department come first
        if (aHasDep && !bHasDep) return -1
        if (!aHasDep && bHasDep) return 1
        return 0
      })

      // Re-append products in sorted order
      productsArray.forEach((product) => {
        productsContainer.appendChild(product)
      })

      // Fade in after sorting
      productsContainer.style.opacity = '1'
    }

    updateTimer(hoursLeftElement, minutesLeftElement, secondsLeftElement) {
      const secondsLeft = Number(secondsLeftElement.dataset.secondsLeft) - 1
      let minutesLeft = Number(minutesLeftElement.dataset.minutesLeft)
      let hoursLeft = Number(hoursLeftElement.dataset.hoursLeft)

      // Update the seconds left
      secondsLeftElement.dataset.secondsLeft = secondsLeft
      this.flashElement(secondsLeftElement, secondsLeft)

      if (hoursLeft === 0 && minutesLeft === 0 && secondsLeft === 0) {
        // Stop the timer
        clearInterval(this.intervalId)
        //console.log('%c timer stopped', 'color: red; font-weight: bold; background-color: black;');
        return
      }

      // If the seconds left is less than 0, update the minutes left
      if (secondsLeft < 0) {
        minutesLeft = minutesLeft - 1
        minutesLeftElement.dataset.minutesLeft = minutesLeft
        this.flashElement(minutesLeftElement, minutesLeft)

        // Reset seconds left to 59
        secondsLeftElement.dataset.secondsLeft = 59
        this.flashElement(secondsLeftElement, 59)
      }

      // If the minutes left is less than 0, update the hours left
      if (minutesLeft < 0) {
        hoursLeft = hoursLeft - 1
        hoursLeftElement.dataset.hoursLeft = hoursLeft
        this.flashElement(hoursLeftElement, hoursLeft)

        // Reset minutes left to 59
        minutesLeftElement.dataset.minutesLeft = 59
        this.flashElement(minutesLeftElement, 59)

        // Reset seconds left to 59
        secondsLeftElement.dataset.secondsLeft = 59
        this.flashElement(secondsLeftElement, 59)
      }
    }

    handleClaimDealButtonClick(productHandle) {
      // Assuming openQuickAddModal is defined in the global scope
      if (typeof openQuickAddModal === 'function') {
        openQuickAddModal(productHandle, [{ key: 'weekly-deal', value: 'true' }])
      }

      fetch(window.Shopify?.routes?.root + 'discount/' + this.discountCodeName)
        .then((response) => response.body)
        .catch((error) => console.error(error))
    }

    flashElement(element, newValue) {
      // Ensure the element has the correct structure
      if (!element.querySelector('.deals__countdown-timer--number')) {
        const number = element.innerHTML
        element.innerHTML = `
          <div class="deals__countdown-timer--bright-bg"></div>
          <div class="deals__countdown-timer--number">${number}</div>
        `
      }

      const numberElement = element.querySelector('.deals__countdown-timer--number')
      element.classList.add('flash-update')

      // Update the number halfway through the animation
      setTimeout(() => {
        numberElement.innerText = String(newValue).padStart(2, '0')
      }, 125) // Half of 0.25s = 125ms

      element.addEventListener(
        'animationend',
        () => {
          element.classList.remove('flash-update')
        },
        { once: true },
      )
    }
  }

  // Register the custom element
  customElements.define('weekly-deals', WeeklyDeals)
}
